import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Code, CodeBlock, PropTable, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'overview', title: 'Overview', level: 2 as const },
  { id: 'conditions', title: 'Filter conditions', level: 2 as const },
  { id: 'field-syntax', title: 'Field syntax', level: 2 as const },
  { id: 'operators', title: 'Operators', level: 2 as const },
  { id: 'param-ref', title: 'Dynamic values (paramRef)', level: 2 as const },
  { id: 'ordering', title: 'Ordering', level: 2 as const },
  { id: 'examples', title: 'Examples', level: 2 as const },
]

export function FiltersOrdering() {
  return (
    <Article toc={toc}>
      <H1>Filters & Ordering</H1>
      <Lead>Coffee API <Code>cup_list</Code> views support powerful filtering and ordering — configured visually in the view wizard with no code.</Lead>

      <H2 id="overview">Overview</H2>
      <P>Filters and ordering are configured in <strong>Step 3</strong> of the view wizard (the columns step) when using <Code>cup_list</Code> mode. They apply server-side — only matching records are returned, and ordering happens in the database query for maximum performance.</P>
      <Screenshot alt="Filter conditions editor showing two conditions: Client.id equals preorderId param, and Status is not empty" caption="The filter conditions editor in the view wizard. Each row is one AND condition." />

      <H2 id="conditions">Filter conditions</H2>
      <P>You can add any number of filter conditions. They are combined with <strong>AND</strong> — all conditions must match for a record to be included.</P>
      <P>Each condition has three parts:</P>
      <UL>
        <LI><strong>Field</strong> — what to filter on (a trait name or a parent relation field)</LI>
        <LI><strong>Operator</strong> — how to compare</LI>
        <LI><strong>Value</strong> — a static value or a reference to an input parameter</LI>
      </UL>

      <H2 id="field-syntax">Field syntax</H2>
      <P>Fields can be:</P>
      <UL>
        <LI><strong>Trait title</strong> — the exact trait name as shown in the Cup detail page, e.g. <Code>Status</Code>, <Code>Price</Code>, <Code>Reference</Code></LI>
        <LI><strong>Parent relation by ID</strong> — <Code>relLabel.id</Code>, e.g. <Code>Client.id</Code> or <Code>exec_shop.id</Code></LI>
        <LI><strong>Parent relation by identifier</strong> — <Code>relLabel.identifier</Code>, e.g. <Code>Client.identifier</Code></LI>
      </UL>
      <Callout type="info">The relation label is exactly the label set when creating the Cup Relation — case-sensitive. Check the Cup detail page Relations tab for the exact label.</Callout>

      <H2 id="operators">Operators</H2>
      <div className="overflow-x-auto rounded-lg border border-slate-200 mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Operator</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Symbol</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Value needed</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-[13px]">
            {[
              ['eq', '=', 'Yes', 'Exact match'],
              ['neq', '≠', 'Yes', 'Not equal'],
              ['contains', 'contains', 'Yes', 'Case-insensitive substring match (ILIKE)'],
              ['gt', '>', 'Yes', 'Greater than (numeric or date comparison)'],
              ['gte', '≥', 'Yes', 'Greater than or equal'],
              ['lt', '<', 'Yes', 'Less than'],
              ['lte', '≤', 'Yes', 'Less than or equal'],
              ['null', 'is empty', 'No', 'Field is null or not set'],
              ['not_null', 'is not empty', 'No', 'Field has a value'],
            ].map(([op, sym, val, desc]) => (
              <tr key={op} className="hover:bg-slate-50 text-slate-600">
                <td className="px-4 py-2 font-mono text-blue-700">{op}</td>
                <td className="px-4 py-2 font-mono">{sym}</td>
                <td className="px-4 py-2">{val}</td>
                <td className="px-4 py-2">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 id="param-ref">Dynamic values (paramRef)</H2>
      <P>Instead of a static value, a condition can reference an input parameter using <Code>paramRef</Code>. The actual value is resolved at runtime from the plate's current inputs.</P>
      <P>In the wizard, tick the <strong>"from param"</strong> checkbox on a condition row and select the param name from the dropdown. If the param has no value at runtime, the condition is silently skipped (rather than filtering for an empty value).</P>
      <Callout type="tip" title="Common use case">
        Filter a list of preorder elements to only those belonging to the current preorder:
        <Code>preorder.id</Code> <Code>=</Code> param <Code>preorderId</Code>.
        When the plate receives <Code>?preorderId=abc-123</Code> in the URL, only records linked to that preorder are shown.
      </Callout>

      <H2 id="ordering">Ordering</H2>
      <P>The ordering section lets you define one or more sort fields. Records are sorted by the first field; ties are broken by the second, and so on.</P>
      <P>Sort fields can be:</P>
      <UL>
        <LI><Code>created_at</Code> — record creation timestamp</LI>
        <LI><Code>updated_at</Code> — record last-modified timestamp</LI>
        <LI>Any Trait title — the bean value for that trait (string comparison by default)</LI>
      </UL>
      <P>Each sort entry has a direction: <Code>ASC</Code> (smallest first) or <Code>DESC</Code> (largest first).</P>
      <Callout type="note">If no ordering is configured, records are sorted by <Code>created_at ASC</Code> (oldest first).</Callout>

      <H2 id="examples">Examples</H2>
      <H3>Show only active customers</H3>
      <CodeBlock language="json" title="Filter: Status = Active">
{`[{ "field": "Status", "op": "eq", "value": "Active" }]`}
      </CodeBlock>

      <H3>Preorder elements for a specific preorder</H3>
      <CodeBlock language="json" title="Filter: parent preorder matches URL param">
{`[{ "field": "preorder.id", "op": "eq", "paramRef": "preorderId" }]`}
      </CodeBlock>

      <H3>Products ordered by price descending</H3>
      <CodeBlock language="json" title="Order by: Price DESC">
{`[{ "field": "Price", "dir": "DESC" }]`}
      </CodeBlock>

      <H3>Invoices with amount &gt; 1000, newest first</H3>
      <CodeBlock language="json" title="Combined filter + order">
{`filterConditions: [{ "field": "Amount", "op": "gt", "value": "1000" }]
orderBy:          [{ "field": "created_at", "dir": "DESC" }]`}
      </CodeBlock>
    </Article>
  )
}
